/**
 * Track B — LightBurn project export.
 * Writes a faithful project copy: preserves all original elements untouched,
 * optionally removes selected-layer shapes (pruning emptied groups), appends
 * one "Pixelated Cxx" Group per layer. Clipped pixels emit as multi-contour
 * Path shapes (VertList/PrimList "L" loops). When cutParams are provided,
 * CutSetting elements are added/updated in child-element Value form.
 * Standalone export (photo workflow / test grids) builds a fresh
 * <LightBurnProject FormatVersion="1"> document.
 */
import type {
  CutParams,
  LayerInfo,
  MopaLayerRecord,
  Pixel,
  Ring,
  SweepParam,
  TestGridSettings,
  VectorExportContext,
} from "../core/types";
import { LIGHTBURN_PALETTE } from "../core/types";
import {
  XmlElement,
  childElements,
  elementText,
  firstChild,
  parseIntD,
  parseXml,
  serializeXml,
} from "./lbrn";
import { pixelShapeRings } from "../core/geometry";

// ---------------------------------------------------------------------------
// Number + string helpers (%.6f, matching the exported-geometry spec exactly).
// ---------------------------------------------------------------------------

function fmt(n: number): string {
  let v = n;
  if (!Number.isFinite(v)) v = 0;
  if (Math.abs(v) < 0.0000005) v = 0;
  const text = v.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  return text.length ? text : "0";
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function pad2(n: number): string {
  return (n < 0 ? "-" : "") + Math.abs(n).toString().padStart(2, "0");
}

function paletteColor(index: number): string {
  return LIGHTBURN_PALETTE[((index % 30) + 30) % 30];
}

function xformText(x: number, y: number, angle = 0): string {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return `${fmt(c)} ${fmt(s)} ${fmt(-s)} ${fmt(c)} ${fmt(x)} ${fmt(y)}`;
}

function addXForm(el: XmlElement, text: string): void {
  const xf = new XmlElement("XForm");
  xf.setText(text);
  el.append(xf);
}

// ---------------------------------------------------------------------------
// Dot / clipped-pixel shape factory (mirrors the reference make_dot_shape_xml).
// ---------------------------------------------------------------------------

function buildDotShape(x: number, y: number, layer: number, shape: string, size: number, aspect = 1, flipY = false): XmlElement {
  const shapeL = shape.toLowerCase();
  const r = size / 2;
  const cut = String(layer);

  // Hearts have no native primitive, and stretched/flipped dots can't be
  // expressed cleanly by the Rect/Ellipse/Polygon writers — emit the exact
  // outline as a Path so the file matches the preview. The common
  // unstretched/unflipped case keeps the tidy native primitives below.
  if (shapeL === "heart" || (aspect > 0 && aspect !== 1) || flipY) {
    return buildClippedPath(layer, pixelShapeRings(shape, x, y, size, { aspect, flipY }));
  }

  if (shapeL === "square") {
    const el = new XmlElement("Shape");
    el.setAttr("Type", "Rect");
    el.setAttr("CutIndex", cut);
    el.setAttr("W", fmt(size));
    el.setAttr("H", fmt(size));
    addXForm(el, `1 0 0 1 ${fmt(x)} ${fmt(y)}`);
    return el;
  }
  if (shapeL === "diamond") {
    // rot 0: a 4-gon with vertices on the axes (a square turned 45deg). PI/4
    // would render an axis-aligned square, which is the separate "square" shape.
    const el = new XmlElement("Shape");
    el.setAttr("Type", "Polygon");
    el.setAttr("CutIndex", cut);
    el.setAttr("Rx", fmt(r));
    el.setAttr("Ry", fmt(r));
    el.setAttr("N", "4");
    addXForm(el, xformText(x, y, 0));
    return el;
  }
  if (shapeL === "hexagon") {
    const el = new XmlElement("Shape");
    el.setAttr("Type", "Polygon");
    el.setAttr("CutIndex", cut);
    el.setAttr("Rx", fmt(r));
    el.setAttr("Ry", fmt(r));
    el.setAttr("N", "6");
    addXForm(el, `1 0 0 1 ${fmt(x)} ${fmt(y)}`);
    return el;
  }
  if (shapeL === "octagon") {
    const el = new XmlElement("Shape");
    el.setAttr("Type", "Polygon");
    el.setAttr("CutIndex", cut);
    el.setAttr("Rx", fmt(r));
    el.setAttr("Ry", fmt(r));
    el.setAttr("N", "8");
    addXForm(el, xformText(x, y, Math.PI / 8));
    return el;
  }
  if (shapeL === "star") {
    // No native star primitive — emit the exact 10-vertex outline as a Path.
    const ring = new Float64Array(20);
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (Math.PI * i) / 5;
      const rr = i % 2 === 0 ? r : r * 0.5;
      ring[i * 2] = x + Math.cos(a) * rr;
      ring[i * 2 + 1] = y + Math.sin(a) * rr;
    }
    return buildClippedPath(layer, [ring]);
  }
  if (shapeL === "triangle") {
    const el = new XmlElement("Shape");
    el.setAttr("Type", "Polygon");
    el.setAttr("CutIndex", cut);
    el.setAttr("Rx", fmt(r));
    el.setAttr("Ry", fmt(r));
    el.setAttr("N", "3");
    addXForm(el, xformText(x, y, -Math.PI / 2));
    return el;
  }
  if (shapeL === "cross") {
    const group = new XmlElement("Shape");
    group.setAttr("Type", "Group");
    group.setAttr("CutIndex", cut);
    addXForm(group, `1 0 0 1 ${fmt(x)} ${fmt(y)}`);
    const children = new XmlElement("Children");
    const bar = Math.max(size * 0.33, size / 6);
    const h = new XmlElement("Shape");
    h.setAttr("Type", "Rect");
    h.setAttr("CutIndex", cut);
    h.setAttr("W", fmt(size));
    h.setAttr("H", fmt(bar));
    addXForm(h, "1 0 0 1 0 0");
    const v = new XmlElement("Shape");
    v.setAttr("Type", "Rect");
    v.setAttr("CutIndex", cut);
    v.setAttr("W", fmt(bar));
    v.setAttr("H", fmt(size));
    addXForm(v, "1 0 0 1 0 0");
    children.append(h);
    children.append(v);
    group.append(children);
    return group;
  }

  // circle + unknown fallback
  const el = new XmlElement("Shape");
  el.setAttr("Type", "Ellipse");
  el.setAttr("CutIndex", cut);
  el.setAttr("Rx", fmt(r));
  el.setAttr("Ry", fmt(r));
  addXForm(el, `1 0 0 1 ${fmt(x)} ${fmt(y)}`);
  return el;
}

/** Clipped pixel: one Path with a VertList over all rings and PrimList "L" loops. */
function buildClippedPath(layer: number, rings: readonly Ring[]): XmlElement {
  const el = new XmlElement("Shape");
  el.setAttr("Type", "Path");
  el.setAttr("CutIndex", String(layer));
  addXForm(el, "1 0 0 1 0 0");
  let vlist = "";
  let plist = "";
  let base = 0;
  for (const ring of rings) {
    const m = ring.length >> 1;
    if (m < 2) continue;
    for (let k = 0; k < m; k++) vlist += `V${fmt(ring[k * 2])} ${fmt(ring[k * 2 + 1])}`;
    for (let k = 0; k < m; k++) plist += `L${base + k} ${base + ((k + 1) % m)}`;
    base += m;
  }
  const vl = new XmlElement("VertList");
  vl.setText(vlist);
  el.append(vl);
  const pl = new XmlElement("PrimList");
  pl.setText(plist);
  el.append(pl);
  return el;
}

function pixelDotSize(pix: Pixel, pixelSize: number): number {
  const factor = Number.isFinite(pix.sizeFactor) ? pix.sizeFactor : 1;
  return Math.max(0.0001, pixelSize * clamp(factor, 0.05, 10));
}

function appendPixelGroups(root: XmlElement, ctx: VectorExportContext): void {
  const byLayer = new Map<number, Pixel[]>();
  for (const p of ctx.pixels) {
    const arr = byLayer.get(p.layer);
    if (arr) arr.push(p);
    else byLayer.set(p.layer, [p]);
  }
  const layers = [...byLayer.keys()].sort((a, b) => a - b);
  for (const layer of layers) {
    const group = new XmlElement("Shape");
    group.setAttr("Type", "Group");
    group.setAttr("CutIndex", String(layer));
    group.setAttr("Name", "Pixelated C" + pad2(layer));
    addXForm(group, "1 0 0 1 0 0");
    const children = new XmlElement("Children");
    for (const pix of byLayer.get(layer)!) {
      if (pix.rings && pix.rings.length) {
        children.append(buildClippedPath(layer, pix.rings));
      } else {
        children.append(buildDotShape(pix.x, pix.y, layer, ctx.settings.shape, pixelDotSize(pix, ctx.settings.pixelSize), ctx.settings.aspect, pix.flipY ?? false));
      }
    }
    group.append(children);
    root.append(group);
  }
}

// ---------------------------------------------------------------------------
// CutSetting construction (child-element Value form).
// ---------------------------------------------------------------------------

function setChildValue(cs: XmlElement, name: string, value: string): void {
  const existing = firstChild(cs, name);
  if (existing) {
    existing.setAttr("Value", value);
    return;
  }
  const el = new XmlElement(name);
  el.setAttr("Value", value);
  cs.append(el);
}

/**
 * Extra per-layer MOPA fields that live in the legend record but not in the
 * base CutParams tuple. When present they are embedded so the burn is fully
 * reproducible from the file alone (see MopaLayerRecord in the shared contract).
 */
interface MopaExtras {
  hatchAngleDeg: number;
  hatchType: MopaLayerRecord["hatchType"];
  focusOffsetMm: number;
}

/**
 * Embed per-layer laser parameters. Galvo/MOPA field names (frequency in Hz,
 * QPulseWidth in ns) are undocumented by LightBurn; the names below match the
 * community-reported spellings and are preserved verbatim on round-trip.
 * `extras` carries the remaining reproducible-record fields (hatch angle/type,
 * focus offset) so the CutSetting alone is a complete per-color recipe.
 */
function applyCutParams(cs: XmlElement, params: CutParams, extras?: MopaExtras): void {
  setChildValue(cs, "maxPower", fmt(params.power));
  setChildValue(cs, "minPower", fmt(params.power));
  setChildValue(cs, "speed", fmt(params.speed));
  setChildValue(cs, "frequency", fmt(params.frequencyKhz * 1000));
  setChildValue(cs, "QPulseWidth", fmt(params.qPulseNs));
  setChildValue(cs, "interval", fmt(params.intervalMm));
  setChildValue(cs, "numPasses", String(Math.max(1, Math.round(params.passes))));
  // Scan/fill controls now come straight from the editable per-layer params.
  setChildValue(cs, "hatchAngle", fmt(params.hatchAngleDeg));
  setChildValue(cs, "crossHatch", params.crossHatch ? "1" : "0");
  setChildValue(cs, "bidir", params.bidir ? "1" : "0");
  // LightBurn fillMode: 0 = fill shapes individually, 2 = fill all at once.
  setChildValue(cs, "fillMode", params.fillAllAtOnce ? "2" : "0");
  if (extras) {
    setChildValue(cs, "zOffset", fmt(extras.focusOffsetMm));
  }
}

function buildCutSetting(index: number, info?: Partial<LayerInfo>, params?: CutParams, typeAttr = "Scan", extras?: MopaExtras): XmlElement {
  const cs = new XmlElement("CutSetting");
  cs.setAttr("type", typeAttr);
  setChildValue(cs, "index", String(index));
  setChildValue(cs, "name", info?.name ?? "C" + pad2(index));
  if (info?.color) setChildValue(cs, "color", info.color);
  if (params) applyCutParams(cs, params, extras);
  return cs;
}

/**
 * Map a per-layer CutParams (+ layer context) to the complete, self-contained
 * MopaLayerRecord that travels alongside every export. This is the single
 * source of truth for the per-color legend; T8 (CSV/SVG) reuses it so the
 * .lbrn2 CutSetting, CSV legend and SVG metadata all agree field-for-field.
 *
 * `runOrder` is 1-based (caller supplies job position). `hatchType` defaults to
 * the deterministic solid-fill "fill" used by MOPA color marking. Missing
 * optional context falls back to the LightBurn palette color / "Cxx" name.
 */
export function cutParamsToMopaRecord(
  index: number,
  runOrder: number,
  params: CutParams,
  opts?: {
    name?: string;
    targetColorHex?: string;
    hatchAngleDeg?: number;
    hatchType?: MopaLayerRecord["hatchType"];
    focusOffsetMm?: number;
  },
): MopaLayerRecord {
  return {
    index,
    runOrder,
    name: opts?.name ?? "C" + pad2(index),
    targetColorHex: opts?.targetColorHex ?? paletteColor(index),
    power: params.power,
    speed: params.speed,
    frequencyKhz: params.frequencyKhz,
    pulseWidthNs: params.qPulseNs,
    hatchSpacingMm: params.intervalMm,
    hatchAngleDeg: opts?.hatchAngleDeg ?? params.hatchAngleDeg,
    hatchType: opts?.hatchType ?? (params.crossHatch ? "cross" : "fill"),
    passes: Math.max(1, Math.round(params.passes)),
    focusOffsetMm: opts?.focusOffsetMm ?? 0,
  };
}

/** Embed a full MopaLayerRecord's worth of fields into a CutSetting element. */
function extrasFromRecord(rec: MopaLayerRecord): MopaExtras {
  return { hatchAngleDeg: rec.hatchAngleDeg, hatchType: rec.hatchType, focusOffsetMm: rec.focusOffsetMm };
}

function cutSettingIndex(cs: XmlElement, order: number): number {
  const attrRaw = cs.attr("index", "Index", "idx", "Idx");
  if (attrRaw !== null) return parseIntD(attrRaw, order);
  const child = firstChild(cs, "index") ?? firstChild(cs, "Index");
  if (child) {
    const v = child.attr("Value", "value");
    if (v !== null) return parseIntD(v, order);
    const t = elementText(child).trim();
    if (t) return parseIntD(t, order);
  }
  return order;
}

function findCutSetting(root: XmlElement, index: number): XmlElement | null {
  let order = 0;
  for (const cs of childElements(root, "CutSetting")) {
    if (cutSettingIndex(cs, order) === index) return cs;
    order++;
  }
  return null;
}

function insertCutSetting(root: XmlElement, cs: XmlElement): void {
  const children = root.children;
  let lastCs = -1;
  let firstShape = -1;
  for (let i = 0; i < children.length; i++) {
    const c = children[i];
    if (c.kind !== "element") continue;
    if (c.localName === "CutSetting") lastCs = i;
    if (firstShape < 0 && c.localName === "Shape") firstShape = i;
  }
  if (lastCs >= 0) children.splice(lastCs + 1, 0, cs);
  else if (firstShape >= 0) children.splice(firstShape, 0, cs);
  else children.push(cs);
}

/** Build the per-layer MOPA extras from export context (layer color/name + params). */
function recordExtrasFor(ctx: VectorExportContext, idx: number, params: CutParams, runOrder: number): MopaExtras {
  const li = ctx.layers[idx];
  const pal = ctx.photoPalette?.find((e) => e.layerIndex === idx);
  const rec = cutParamsToMopaRecord(idx, runOrder, params, {
    name: li?.name,
    targetColorHex: pal?.targetColor ?? li?.color,
  });
  return extrasFromRecord(rec);
}

function embedCutParams(root: XmlElement, ctx: VectorExportContext): void {
  const cutParams = ctx.cutParams;
  if (!cutParams) return;
  const ordered = Object.keys(cutParams)
    .map(Number)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  let runOrder = 1;
  for (const idx of ordered) {
    const params = cutParams[idx];
    const extras = recordExtrasFor(ctx, idx, params, runOrder++);
    const existing = findCutSetting(root, idx);
    if (existing) applyCutParams(existing, params, extras);
    else insertCutSetting(root, buildCutSetting(idx, ctx.layers[idx], params, "Scan", extras));
  }
}

// ---------------------------------------------------------------------------
// Selected-shape removal (recursive walk, prunes emptied groups).
// ---------------------------------------------------------------------------

function readCut(el: XmlElement, inherited: number | null): number | null {
  const raw = el.attr("CutIndex", "cutindex", "cutIndex");
  if (raw === null) return inherited;
  const n = parseFloat(raw.trim());
  return Number.isFinite(n) ? Math.trunc(n) : inherited;
}

/** Guard against pathologically deep group nesting overflowing the stack. */
const MAX_NEST_DEPTH = 512;

function removeSelectedShapes(
  parent: XmlElement,
  selected: ReadonlySet<number>,
  inheritedCut: number | null,
  depth: number,
): boolean {
  if (depth >= MAX_NEST_DEPTH) {
    // Too deep to safely recurse; leave this subtree untouched rather than
    // risk a stack overflow. A copy export of a hostile file stays intact.
    return childElements(parent).some((c) => c.localName === "Shape");
  }
  const children = parent.children;
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    if (child.kind !== "element" || child.localName !== "Shape") continue;
    const t = child.attr("Type", "type") ?? "";
    const cut = readCut(child, inheritedCut);
    if (t === "Group") {
      const container = firstChild(child, "Children");
      if (container) {
        const hasShapes = removeSelectedShapes(container, selected, cut, depth + 1);
        if (!hasShapes) children.splice(i, 1);
      } else if (cut !== null && selected.has(cut)) {
        children.splice(i, 1);
      } else {
        removeSelectedShapes(child, selected, cut, depth + 1);
      }
    } else if (cut !== null && selected.has(cut)) {
      children.splice(i, 1);
    }
  }
  return childElements(parent).some((c) => c.localName === "Shape");
}

// ---------------------------------------------------------------------------
// Public exports.
// ---------------------------------------------------------------------------

function newProjectRoot(): XmlElement {
  const root = new XmlElement("LightBurnProject");
  root.setAttr("AppVersion", "1.7.00");
  root.setAttr("FormatVersion", "1");
  root.setAttr("MaterialHeight", "0");
  root.setAttr("MirrorX", "False");
  root.setAttr("MirrorY", "False");
  return root;
}

/** Export dots back into a copy of the original project XML. Returns the XML text. */
export function exportLbrnCopy(
  originalXml: string,
  ctx: VectorExportContext,
  options: { replaceOriginal: boolean; selectedLayers: ReadonlySet<number> },
): string {
  const root = parseXml(originalXml);
  if (!root) return exportLbrnStandalone(ctx);
  if (options.replaceOriginal) removeSelectedShapes(root, options.selectedLayers, null, 0);
  embedCutParams(root, ctx);
  appendPixelGroups(root, ctx);
  return serializeXml(root);
}

/** Build a standalone .lbrn2 project from pixels (photo workflow export). */
export function exportLbrnStandalone(ctx: VectorExportContext): string {
  const root = newProjectRoot();
  const paletteByLayer = new Map<number, { name: string; color: string }>();
  if (ctx.photoPalette) {
    for (const e of ctx.photoPalette) {
      paletteByLayer.set(e.layerIndex, { name: "C" + pad2(e.layerIndex) + " photo", color: e.svgColor });
    }
  }
  const used = [...new Set(ctx.pixels.map((p) => p.layer))].sort((a, b) => a - b);
  let runOrder = 1;
  for (const layer of used) {
    const pal = paletteByLayer.get(layer);
    const li = ctx.layers[layer];
    const info: Partial<LayerInfo> = pal ?? (li ? { name: li.name, color: li.color } : { name: "C" + pad2(layer), color: paletteColor(layer) });
    const params = ctx.cutParams?.[layer];
    const extras = params ? recordExtrasFor(ctx, layer, params, runOrder++) : undefined;
    root.append(buildCutSetting(layer, info, params, "Scan", extras));
  }
  appendPixelGroups(root, ctx);
  return serializeXml(root);
}

function cellParams(base: CutParams, rowParam: SweepParam, rowVal: number, colParam: SweepParam, colVal: number): CutParams {
  const p: CutParams = { ...base };
  p[rowParam] = rowVal;
  p[colParam] = colVal;
  return p;
}

function paramSummary(p: CutParams): string {
  return `P${fmt(p.power)} S${fmt(p.speed)} F${fmt(p.frequencyKhz)} Q${fmt(p.qPulseNs)} I${fmt(p.intervalMm)}`;
}

/** Ready-to-run parameter sweep grid as .lbrn2 (one CutSetting per cell). */
export function exportTestGrid(settings: TestGridSettings): string {
  const root = newProjectRoot();
  const rows = settings.rowValues.slice(0, 8);
  const cols = settings.colValues.slice(0, 8);
  const swatch = settings.swatchSizeMm > 0 ? settings.swatchSizeMm : 8;
  const gap = Math.max(1, settings.gapMm);
  const step = swatch + gap;

  const cutSettings: XmlElement[] = [];
  const shapes: XmlElement[] = [];
  let idx = 0;
  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < cols.length; c++) {
      const params = cellParams(settings.base, settings.rowParam, rows[r], settings.colParam, cols[c]);
      const name = `R${r + 1}C${c + 1} ${paramSummary(params)}`;
      cutSettings.push(buildCutSetting(idx, { name }, params));
      const rect = new XmlElement("Shape");
      rect.setAttr("Type", "Rect");
      rect.setAttr("CutIndex", String(idx));
      rect.setAttr("W", fmt(swatch));
      rect.setAttr("H", fmt(swatch));
      addXForm(rect, `1 0 0 1 ${fmt(c * step + swatch / 2)} ${fmt(r * step + swatch / 2)}`);
      shapes.push(rect);
      idx++;
    }
  }

  for (const cs of cutSettings) root.append(cs);
  if (settings.labelCells) {
    const notes = new XmlElement("Notes");
    notes.setAttr("ShowOnLoad", "1");
    notes.setAttr(
      "Notes",
      [
        "MOPA test grid — starting points only, requires calibration.",
        `Rows (${settings.rowParam}): ${rows.join(", ")}`,
        `Cols (${settings.colParam}): ${cols.join(", ")}`,
        `Base: ${paramSummary(settings.base)}`,
      ].join("\n"),
    );
    root.append(notes);
  }
  for (const sh of shapes) root.append(sh);
  return serializeXml(root);
}
